import { useTranslation } from "@sagot/i18n";
import { Field, Icon, Textarea, cn } from "@sagot/ui";
import { useFormContext, useWatch } from "react-hook-form";
import { WHEN_TO_USE_MAX, type ActionFormValues } from "./schema-model";

const POINTS = ["p1", "p2", "p3", "p4"] as const;

interface WhenToUseFieldProps {
  disabled: boolean;
  /** Start with the guide open (a new action); an existing one keeps it folded away. */
  guideOpen: boolean;
}

/**
 * "When to use": the text the AI reads to choose this action, so the form helps write it well: a short
 * checklist and a store example in a fold-away guide, and a character counter against the server limit.
 */
export function WhenToUseField({ disabled, guideOpen }: WhenToUseFieldProps) {
  const { t } = useTranslation();
  const form = useFormContext<ActionFormValues>();
  const used = useWatch({ control: form.control, name: "when_to_use" }).length;
  const over = used > WHEN_TO_USE_MAX;
  const hint = (
    <>
      {t("build.actions.editor.whenToUseHint")}{" "}
      <span data-testid="when-to-use-count" className={cn("tabular-nums", over && "font-semibold text-danger")}>
        {t("build.actions.editor.whenToUseCount", { used, max: WHEN_TO_USE_MAX })}
      </span>
    </>
  );
  return (
    <div className="space-y-2">
      <Field label={t("build.actions.editor.whenToUse")} hint={hint} error={form.formState.errors.when_to_use?.message}>
        <Textarea rows={3} disabled={disabled} {...form.register("when_to_use")} />
      </Field>
      <details open={guideOpen || undefined} className="rounded-md border border-line bg-surface-2 p-3 text-xs" data-testid="when-to-use-guide">
        <summary className="flex cursor-pointer items-center gap-2 font-semibold text-fg">
          <Icon name="info" className="size-4 shrink-0 text-primary-ink" />
          {t("build.actions.editor.whenToUseHelp.summary")}
        </summary>
        <p className="mt-2 text-fg-muted">{t("build.actions.editor.whenToUseHelp.intro")}</p>
        <ol className="mt-2 list-decimal space-y-1 pl-5 text-fg-muted">
          {POINTS.map((point) => <li key={point}>{t(`build.actions.editor.whenToUseHelp.${point}`)}</li>)}
        </ol>
        <p className="mt-3 font-semibold text-fg">{t("build.actions.editor.whenToUseHelp.exampleTitle")}</p>
        <blockquote className="mt-1 rounded-md border-l-2 border-primary bg-surface p-2 text-fg-muted" data-testid="when-to-use-example">
          {t("build.actions.editor.whenToUseHelp.example")}
        </blockquote>
      </details>
    </div>
  );
}
